import isEqual from 'lodash/isEqual';
import * as Yup from 'yup';
import { MedicationStockInputDto, MedicationStockIssueDto } from 'woofwoof-api';
import { translateKey } from 'src/locales/translate';
import {
  StockHandover,
  StockMeasure,
  parseStockQuantity,
  toStockInput,
} from 'src/sections/@dashboard/warehouse/utils/clinicalStock';

export type StockPrescriptionChoice = '' | 'ISSUED' | 'NOT_ISSUED';

/** Blank while the vet has not said how an Rx drug given once left (P7B-R10). */
export type StockHandoverChoice = StockHandover | '';

export interface MedicationStockFormValues {
  stockEnabled: boolean;
  stockProductId: string;
  /**
   * The text field holds the typed string until the resolver casts it on
   * submit; read a watched value through `requestedMilli`/`parseStockQuantity`.
   */
  stockQuantity: number | '';
  stockMeasure: StockMeasure;
  stockHandover: StockHandoverChoice;
  stockBatchId: string;
  /** Handed-over Rx only: was a prescription issued (review D4). */
  stockPrescription: StockPrescriptionChoice;
  /** Handed-over only: who received the drug; blank lets the backend name the owner. */
  stockRecipientName: string;
  /**
   * Handed-over only: the recipient's address (#688 R1-2); blank lets the
   * backend record the owner's profile address.
   */
  stockRecipientAddress: string;
  /**
   * Whether the Product is Rx: drives the rule above and whether the payload
   * carries the prescription (P7B-R1). Seeded from a saved answer, then set
   * from the Product once it is read (`MedicationStockIssueDefault`).
   */
  stockProductIsRx: boolean;
  /** The saved medication has the clinic's MANUAL issue, so switching off must return it. */
  stockWasSet: boolean;
}

export const EMPTY_STOCK_VALUES: MedicationStockFormValues = {
  stockEnabled: false,
  stockProductId: '',
  stockQuantity: '',
  stockMeasure: 'UNIT',
  stockHandover: 'ADMINISTERED',
  stockBatchId: '',
  stockPrescription: '',
  stockRecipientName: '',
  stockRecipientAddress: '',
  stockProductIsRx: false,
  stockWasSet: false,
};

/** Mirrors `MedicationStockInputDto.recipientName` (`@MaxLength(120)`). */
export const STOCK_RECIPIENT_MAX = 120;

/** Mirrors `MedicationStockInputDto.recipientAddress` (`@MaxLength(300)`). */
export const STOCK_RECIPIENT_ADDRESS_MAX = 300;

/** Validated with the "Magazyn" step, the only one showing them (#692 rev B). */
export const STOCK_FIELD_NAMES: (keyof MedicationStockFormValues)[] = [
  'stockEnabled',
  'stockProductId',
  'stockQuantity',
  'stockMeasure',
  'stockHandover',
  'stockBatchId',
  'stockPrescription',
  'stockRecipientName',
  'stockRecipientAddress',
];

/** The form's prescription radio for a stored `prescriptionIssued`. */
export function prescriptionChoice(value?: boolean): StockPrescriptionChoice {
  if (value === true) return 'ISSUED';
  if (value === false) return 'NOT_ISSUED';
  return '';
}

/** #690: a Warehouse pick in the medicine search takes that Product; FEFO picks the Batch. */
export function preselectedStockValues(
  productId: string,
): MedicationStockFormValues {
  return {
    ...EMPTY_STOCK_VALUES,
    stockEnabled: true,
    stockProductId: productId,
  };
}

/** The API sends only the viewing clinic's issues; the form edits its MANUAL one. */
export function buildStockDefaults(
  issues: MedicationStockIssueDto[] | undefined,
): MedicationStockFormValues {
  const manual = (issues ?? []).find((issue) => issue.origin === 'MANUAL');
  if (!manual) return EMPTY_STOCK_VALUES;
  const input = toStockInput(manual.quantityMilli);
  return {
    stockEnabled: true,
    stockProductId: manual.productId,
    stockQuantity: input.quantity,
    stockMeasure: input.measure,
    stockHandover: manual.handover,
    stockBatchId: manual.batchId ?? '',
    stockPrescription: prescriptionChoice(manual.prescriptionIssued),
    stockRecipientName: manual.recipientName ?? '',
    stockRecipientAddress: manual.recipientAddress ?? '',
    stockProductIsRx: savedAsRx(manual.handover, manual.prescriptionIssued),
    stockWasSet: true,
  };
}

/**
 * A prescription answer is recorded only for an Rx drug handed over, so a
 * saved one says the Product was Rx before its detail is read — and the
 * payload repeats the answer even if that read never comes (P7B-R1).
 */
export function savedAsRx(
  handover: StockHandover | undefined,
  prescriptionIssued: boolean | undefined,
): boolean {
  return handover === 'HANDED_OVER' && typeof prescriptionIssued === 'boolean';
}

/**
 * P7B-R3: the Product of the taking the form opened with — a saved one, or
 * a line with its own quantity — which is the vet's answer and never
 * recomputed; null when the form opened without one (nothing, or a pick
 * still to be worked out).
 */
export function openedTakingProductId(
  defaults:
    | Readonly<
        Partial<
          Pick<
            MedicationStockFormValues,
            'stockProductId' | 'stockQuantity' | 'stockWasSet'
          >
        >
      >
    | undefined,
): string | null {
  if (!defaults?.stockProductId) return null;
  const answered =
    Boolean(defaults.stockWasSet) ||
    parseStockQuantity(defaults.stockQuantity ?? '') !== null;
  return answered ? defaults.stockProductId : null;
}

/** An empty number input arrives as `''`; Yup would otherwise call it NaN. */
const optionalNumber = () =>
  Yup.number().transform((value, original) =>
    original === '' || original === null ? undefined : value,
  );

/** The backend takes at most three decimals (`maxDecimalPlaces: 3`). */
function hasAtMostThreeDecimals(value: number | undefined): boolean {
  if (value === undefined) return true;
  const milli = value * 1000;
  return Math.abs(milli - Math.round(milli)) <= 1e-6;
}

export const medicationStockSchemaFields = {
  stockEnabled: Yup.boolean().required(),
  stockProductId: Yup.string().when('stockEnabled', {
    is: true,
    then: (schema) =>
      schema.required(translateKey('warehouse.clinical.productRequired')),
    otherwise: (schema) => schema.optional(),
  }),
  stockQuantity: optionalNumber().when('stockEnabled', {
    is: true,
    then: (schema) =>
      schema
        .moreThan(0, translateKey('warehouse.clinical.quantityPositive'))
        .test(
          'stock-quantity-precision',
          translateKey('warehouse.errors.INVALID_QUANTITY'),
          hasAtMostThreeDecimals,
        )
        .required(translateKey('warehouse.clinical.quantityRequired')),
    otherwise: (schema) => schema.optional(),
  }),
  stockMeasure: Yup.string().oneOf(['PACKAGE', 'UNIT']).required(),
  // P7B-R10: nothing is assumed for an Rx drug given once, so a taking
  // waits for the vet's answer.
  stockHandover: Yup.string().when('stockEnabled', {
    is: true,
    then: (schema) =>
      schema
        .oneOf(
          ['ADMINISTERED', 'HANDED_OVER'],
          translateKey('warehouse.clinical.handoverRequired'),
        )
        .required(translateKey('warehouse.clinical.handoverRequired')),
    otherwise: (schema) => schema.optional(),
  }),
  stockBatchId: Yup.string().optional(),
  stockPrescription: Yup.string().when(
    ['stockEnabled', 'stockHandover', 'stockProductIsRx'],
    {
      is: (enabled: boolean, handover: string, isRx: boolean) =>
        enabled && handover === 'HANDED_OVER' && isRx,
      then: (schema) =>
        schema
          .oneOf(
            ['ISSUED', 'NOT_ISSUED'],
            translateKey('warehouse.clinical.prescriptionRequired'),
          )
          .required(translateKey('warehouse.clinical.prescriptionRequired')),
      otherwise: (schema) => schema.optional(),
    },
  ),
  stockRecipientName: Yup.string().max(
    STOCK_RECIPIENT_MAX,
    translateKey('warehouse.clinical.recipientTooLong'),
  ),
  // #688 R1-2: a handed-over Rx drug records its recipient's address. Only
  // the owner has one to fall back to (the backend reads their profile), so
  // it is required here once another person is named.
  stockRecipientAddress: Yup.string()
    .max(
      STOCK_RECIPIENT_ADDRESS_MAX,
      translateKey('warehouse.clinical.recipientAddressTooLong'),
    )
    .when(
      [
        'stockEnabled',
        'stockHandover',
        'stockProductIsRx',
        'stockRecipientName',
      ],
      {
        is: (
          enabled: boolean,
          handover: string,
          isRx: boolean,
          recipientName?: string,
        ) =>
          enabled &&
          handover === 'HANDED_OVER' &&
          isRx &&
          Boolean(recipientName?.trim()),
        then: (schema) =>
          schema
            .trim()
            .required(
              translateKey('warehouse.clinical.recipientAddressRequired'),
            ),
        otherwise: (schema) => schema.optional(),
      },
    ),
  stockProductIsRx: Yup.boolean().required(),
  stockWasSet: Yup.boolean().required(),
};

/**
 * `undefined` leaves stock alone; `null` returns a saved taking, and only
 * when the vet switched "Pobierz z magazynu" off: an incomplete taking with
 * the switch on (the schema blocks it) never reverses the saved one.
 */
export function buildStockPayload(
  values: MedicationStockFormValues,
): MedicationStockInputDto | null | undefined {
  if (!values.stockEnabled) return values.stockWasSet ? null : undefined;
  const quantity = parseStockQuantity(values.stockQuantity);
  const handover = values.stockHandover;
  if (!values.stockProductId || quantity === null || !handover) {
    return undefined;
  }
  const handedOver = handover === 'HANDED_OVER';
  const recipientName = values.stockRecipientName.trim();
  const recipientAddress = values.stockRecipientAddress.trim();
  return {
    productId: values.stockProductId,
    quantity,
    measure: values.stockMeasure,
    handover,
    ...(values.stockBatchId ? { batchId: values.stockBatchId } : {}),
    // P7B-R1: a drug that is not Rx records no prescription, whatever the
    // field still holds from another drug.
    ...(handedOver && values.stockProductIsRx && values.stockPrescription
      ? { prescriptionIssued: values.stockPrescription === 'ISSUED' }
      : {}),
    ...(handedOver && recipientName ? { recipientName } : {}),
    ...(handedOver && recipientAddress ? { recipientAddress } : {}),
  };
}

/**
 * P7B-R5: the form still holds the saved taking — a save would send it as it
 * was, its prescription answer aside — so the backend reads a repeat
 * (`repeatsStoredIssue`) and compares that answer with the stored one.
 * Never for a taking with nothing stored: a new pick or a template's line.
 */
export function repeatsSavedTaking(
  values: MedicationStockFormValues,
  saved: Readonly<Partial<MedicationStockFormValues>> | undefined,
): boolean {
  if (!saved?.stockWasSet) return false;
  const sent = (
    stock: MedicationStockFormValues,
  ): MedicationStockInputDto | null | undefined =>
    buildStockPayload({ ...stock, stockPrescription: '' });
  return isEqual(sent(values), sent({ ...EMPTY_STOCK_VALUES, ...saved }));
}

export function buildStockEntry(values: MedicationStockFormValues): {
  stock?: MedicationStockInputDto | null;
} {
  const stock = buildStockPayload(values);
  return stock === undefined ? {} : { stock };
}
